import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiFetch } from './client';
import { queryKeys } from './queryKeys';
import type { CreateJobRequest, CreateJobResponse, Job, JobEventRequest } from '../types';

// `useJobs` no longer polls — `useUserJobsSubscription` (SSE) pushes the live
// jobs list into the cache under `queryKeys.jobs(userId)`. This hook stays as a
// cache reader so components keep the same call site; the initial `queryFn`
// seeds the cache before the first SSE snapshot lands.
export function useJobs(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.jobs(userId),
    queryFn: userId
      ? () => apiFetch<Job[]>(`/api/v1/jobs?userId=${encodeURIComponent(userId)}`)
      : skipToken,
    placeholderData: keepPreviousData,
  });
}

export function useJob(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.job(id),
    queryFn: id ? () => apiFetch<Job>(`/api/v1/jobs/${encodeURIComponent(id)}`) : skipToken,
  });
}

// Mutation hooks (A1EE-338). Mirror the api/admin.ts pattern: useMutation +
// apiFetch + queryClient.invalidateQueries. SSE already pushes fresh job state
// after the transition; the invalidate is the belt-and-suspenders path for the
// immediate response (and for a state the SSE snapshot hasn't repainted yet).

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export function useCreateJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateJobRequest) =>
      apiFetch<CreateJobResponse>('/api/v1/jobs', {
        method: 'POST',
        body: JSON.stringify(request),
        headers: JSON_HEADERS,
      }),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.jobs(data.job.userId) });
    },
  });
}

export function useDispatchJobEvent(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: JobEventRequest) =>
      apiFetch<CreateJobResponse>(`/api/v1/jobs/${encodeURIComponent(jobId)}/events`, {
        method: 'POST',
        body: JSON.stringify(request),
        headers: JSON_HEADERS,
      }),
    onSuccess: async () => {
      // Invalidate the single-job reader and the jobs-list prefix (we don't know
      // the userId here without threading it; the prefix covers ['jobs', <any>]).
      await queryClient.invalidateQueries({ queryKey: queryKeys.job(jobId) });
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

export interface CreateInteractiveJobArgs {
  userId: string;
  name?: string;
  repo?: string;
  autostart: boolean;
}

// Interactive create+start is orchestrated client-side: a1ec never auto-starts
// an INTERACTIVE job on create (A1EE-388 D5), so a one-click "start a VM" is
// POST /jobs then POST /jobs/{id}/events {StartJob}. Both fetches live in one
// mutationFn because the started job's id isn't known until create resolves.
export function useCreateInteractiveJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      name,
      repo,
      autostart,
    }: CreateInteractiveJobArgs): Promise<CreateJobResponse> => {
      const createBody: CreateJobRequest = { userId, mode: 'INTERACTIVE' };
      if (name) createBody.name = name;
      if (repo) createBody.repo = repo;
      const created = await apiFetch<CreateJobResponse>('/api/v1/jobs', {
        method: 'POST',
        body: JSON.stringify(createBody),
        headers: JSON_HEADERS,
      });
      if (!autostart) return created;
      // 202 (transient/fatal dispatch) is a 2xx, so apiFetch returns the body
      // with `dispatchError` set instead of throwing — the caller inspects runId
      // vs dispatchError to decide where to navigate.
      return apiFetch<CreateJobResponse>(
        `/api/v1/jobs/${encodeURIComponent(created.job.id)}/events`,
        { method: 'POST', body: JSON.stringify({ event: 'StartJob' }), headers: JSON_HEADERS },
      );
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.jobs(data.job.userId) });
    },
  });
}
